/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  FolderGit2,
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  Check,
  Star,
  ExternalLink,
  PlusCircle,
  MinusCircle,
} from 'lucide-react';
import axiosClient from '../api/axiosClient';
import toast from 'react-hot-toast';

const PROJECT_TYPES = [
  'Mobile Apps',
  'Web Development',
  'Cloud Infrastructure',
  'Cybersecurity & Compliance',
  'Managed IT & Automation',
  'Disaster Recovery Architecture',
];

export default function PortfolioManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('ALL');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    type: 'Cloud Infrastructure',
    clientName: '',
    imageUrl: '',
    creditUrl: '',
    featured: false,
    responsibilities: ['Infrastructure provisioning and architecture design', '24/7 Monitoring implementation'],
  });

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const res = await axiosClient.get('/portfolio');
      setItems(res.data);
    } catch (err) {
      toast.error('Failed to load portfolio items.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      let parsedResp = [];
      try {
        parsedResp = typeof item.responsibilities === 'string' ? JSON.parse(item.responsibilities) : (item.responsibilities || []);
      } catch (e) {
        parsedResp = [item.responsibilities || ''];
      }

      setFormData({
        title: item.title,
        slug: item.slug,
        type: item.type || 'Cloud Infrastructure',
        clientName: item.clientName || '',
        imageUrl: item.imageUrl || '',
        creditUrl: item.creditUrl || '',
        featured: !!item.featured,
        responsibilities: parsedResp.length ? parsedResp : ['Key project delivery milestone'],
      });
    } else {
      setEditingItem(null);
      setFormData({
        title: '',
        slug: '',
        type: 'Cloud Infrastructure',
        clientName: '',
        imageUrl: '',
        creditUrl: '',
        featured: false,
        responsibilities: ['Architecture roadmap and implementation', 'Automated testing and security scan'],
      });
    }
    setModalOpen(true);
  };

  const handleTitleChange = (val) => {
    setFormData(prev => ({
      ...prev,
      title: val,
      slug: prev.slug && editingItem ? prev.slug : val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
    }));
  };

  const handleAddResponsibility = () => {
    setFormData(prev => ({
      ...prev,
      responsibilities: [...prev.responsibilities, ''],
    }));
  };

  const handleUpdateResponsibility = (idx, text) => {
    const updated = [...formData.responsibilities];
    updated[idx] = text;
    setFormData({ ...formData, responsibilities: updated });
  };

  const handleRemoveResponsibility = (idx) => {
    const updated = [...formData.responsibilities];
    updated.splice(idx, 1);
    setFormData({ ...formData, responsibilities: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.slug) {
      toast.error('Title and Slug are required.');
      return;
    }

    try {
      const payload = {
        ...formData,
        responsibilities: formData.responsibilities.filter(r => r.trim().length > 0),
      };

      if (editingItem) {
        await axiosClient.put(`/portfolio/${editingItem.id}`, payload);
        toast.success('Portfolio item updated successfully.');
      } else {
        await axiosClient.post('/portfolio', payload);
        toast.success('Portfolio item created successfully.');
      }
      setModalOpen(false);
      fetchPortfolio();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save portfolio item.');
    }
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await axiosClient.delete(`/portfolio/${id}`);
        toast.success('Portfolio item deleted.');
        fetchPortfolio();
      } catch (err) {
        toast.error('Failed to delete portfolio item.');
      }
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.clientName && item.clientName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      item.type.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'ALL' || item.type === selectedType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight flex items-center space-x-2">
            <FolderGit2 className="text-indigo-400" />
            <span>Portfolio & Case Studies</span>
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Showcase enterprise success stories, client deliveries, and technical implementations.
          </p>
        </div>

        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2.5 rounded-xl font-bold text-xs bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-600/30 flex items-center space-x-2 transition-all self-start sm:self-auto"
        >
          <Plus size={16} />
          <span>Add Project / Case Study</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900/60 p-4 rounded-2xl border border-slate-800">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Search projects, clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
          />
        </div>

        {/* Category Filters */}
        <div className="flex items-center space-x-2 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
          <button
            onClick={() => setSelectedType('ALL')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
              selectedType === 'ALL'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            All Types
          </button>
          {PROJECT_TYPES.map(type => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                selectedType === type
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* Portfolio Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/40 rounded-2xl border border-slate-800/60">
          <FolderGit2 className="mx-auto text-slate-600 mb-3" size={40} />
          <h3 className="text-sm font-bold text-slate-300">No portfolio items found</h3>
          <p className="text-xs text-slate-500 mt-1">Start showcasing your customer transformations.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => {
            let resps = [];
            try {
              resps = typeof item.responsibilities === 'string' ? JSON.parse(item.responsibilities) : (item.responsibilities || []);
            } catch (e) {
              resps = [];
            }

            return (
              <div
                key={item.id}
                className="bg-slate-900 border border-slate-800/80 hover:border-slate-700 rounded-2xl overflow-hidden shadow-xl transition-all duration-200 flex flex-col justify-between group"
              >
                <div>
                  {/* Image Cover */}
                  <div className="relative h-44 w-full bg-slate-950 overflow-hidden">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-600">
                        <FolderGit2 size={36} />
                      </div>
                    )}
                    {item.featured && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-amber-500/90 text-slate-950 font-black text-[10px] uppercase tracking-wider flex items-center space-x-1 shadow-lg">
                        <Star size={12} fill="currentColor" />
                        <span>Featured</span>
                      </div>
                    )}
                    <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-lg bg-slate-950/80 backdrop-blur-md border border-slate-800 text-[11px] font-semibold text-indigo-300">
                      {item.type}
                    </div>
                  </div>

                  {/* Body Content */}
                  <div className="p-5">
                    {item.clientName && (
                      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1">
                        Client: <span className="text-white">{item.clientName}</span>
                      </div>
                    )}
                    <h3 className="text-base font-bold text-white group-hover:text-indigo-400 transition-colors">
                      {item.title}
                    </h3>

                    {/* Scope / Responsibilities */}
                    {resps.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {resps.slice(0, 3).map((r, i) => (
                          <div key={i} className="text-xs text-slate-300 flex items-start space-x-2">
                            <span className="text-indigo-400 font-bold">•</span>
                            <span className="line-clamp-1">{r}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer Controls */}
                <div className="px-5 py-3 bg-slate-950/60 border-t border-slate-800/80 flex items-center justify-between text-xs">
                  {item.creditUrl ? (
                    <a
                      href={item.creditUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-slate-400 hover:text-indigo-400 flex items-center space-x-1 transition-colors"
                    >
                      <span>Live Reference</span>
                      <ExternalLink size={12} />
                    </a>
                  ) : (
                    <span className="text-slate-500 font-mono text-[11px]">Internal Project</span>
                  )}

                  <div className="flex items-center space-x-1.5">
                    <button
                      onClick={() => handleOpenModal(item)}
                      className="p-1.5 rounded-lg bg-slate-800 hover:bg-indigo-600/20 text-slate-300 hover:text-indigo-400 transition-colors"
                      title="Edit Project"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id, item.title)}
                      className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-600/20 text-slate-300 hover:text-rose-400 transition-colors"
                      title="Delete Project"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit / Create Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-white">
                  {editingItem ? `Edit Project: ${editingItem.title}` : 'Add New Portfolio Project'}
                </h2>
                <p className="text-xs text-slate-400">Configure case study showcase, client info, and delivered scope</p>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4 flex-1 custom-scrollbar">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Project Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="e.g. Cloud Infrastructure Migration for FinTech"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Client / Organization Name</label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    placeholder="e.g. Apex Global Banking"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Project Domain / Category</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-indigo-500"
                  >
                    {PROJECT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Cover Image URL</label>
                  <input
                    type="text"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    placeholder="https://images.unsplash.com/..."
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Live Link / Case Study URL</label>
                  <input
                    type="text"
                    value={formData.creditUrl}
                    onChange={(e) => setFormData({ ...formData, creditUrl: e.target.value })}
                    placeholder="https://client-domain.com"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div className="pt-5">
                  <label className="flex items-center space-x-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-4 h-4 rounded text-indigo-600 bg-slate-950 border-slate-800 focus:ring-0"
                    />
                    <span className="text-xs font-semibold text-slate-300">Feature on Homepage Case Studies</span>
                  </label>
                </div>
              </div>

              {/* Responsibilities list */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-slate-300">Project Scope & Deliverables</label>
                  <button
                    type="button"
                    onClick={handleAddResponsibility}
                    className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center space-x-1"
                  >
                    <PlusCircle size={14} />
                    <span>Add Item</span>
                  </button>
                </div>

                <div className="space-y-2">
                  {formData.responsibilities.map((resp, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={resp}
                        onChange={(e) => handleUpdateResponsibility(idx, e.target.value)}
                        placeholder={`Scope item #${idx + 1}...`}
                        className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                      />
                      {formData.responsibilities.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveResponsibility(idx)}
                          className="p-2 text-slate-400 hover:text-rose-400"
                        >
                          <MinusCircle size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="pt-4 border-t border-slate-800 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-600/30 flex items-center space-x-2 transition-all"
                >
                  <Check size={16} />
                  <span>{editingItem ? 'Save Changes' : 'Create Project'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
